/**
 * Drill diagram accessors.
 *
 * A drill's diagram has been three different shapes over its life: a bare SVG
 * string, `{svg}` from an upload or a playbook pick, and now `{svg, play}`
 * where `play` is live geometry that can be re-opened and animated. Reading
 * goes through here so no caller has to know which one it has, and nothing is
 * ever migrated in place — a drill that only has an SVG keeps rendering from
 * that SVG for as long as it exists.
 */
import type { DrillData, DrillDiagramEntry, PlayData } from '../types';
import { PB_DRILL_H, PB_DRILL_W } from './constants';
import { buildDrillSVG } from './svg';

/** The flattened picture, for display and print. Always present after a save. */
export function diagramSvg(d: DrillData | null | undefined): string | null {
  const g = d?.diagram;
  if (!g) return null;
  if (typeof g === 'string') return g.trimStart().toLowerCase().startsWith('<svg') || g.trimStart().startsWith('<?xml') ? g : null;
  return g.svg ?? null;
}

/** Live geometry, when the diagram was drawn (or copied) rather than pasted. */
export function diagramPlay(d: DrillData | null | undefined): PlayData | null {
  const g = d?.diagram;
  if (!g || typeof g === 'string') return null;
  const p = g.play;
  if (!p || !Array.isArray(p.players) || !Array.isArray(p.lines)) return null;
  return p;
}

export function blankDrillPlay(id: string, name = ''): PlayData {
  return { id, name, players: [], lines: [], cardMode: 'skill', playType: 'drill' };
}

/**
 * Build the stored diagram for a drill.
 *
 * Writes the rendered SVG alongside the geometry every time, so a reader that
 * only understands `svg` — the print path, an older build of the app, anything
 * the importer produced — sees a diagram that matches what the editor showed.
 */
export function drillDiagram(play: PlayData, playId?: string): { svg: string; play: PlayData; playId?: string; cardMode: string } {
  return {
    svg: buildDrillSVG(play),
    play,
    ...(playId ? { playId } : {}),
    cardMode: play.cardMode,
  };
}

export const DRILL_CANVAS = { w: PB_DRILL_W, h: PB_DRILL_H };

/* ── Several diagrams on one drill ─────────────────────────────────────── */

/** One diagram as callers see it: always an object, never a bare string. */
export interface DiagramView {
  label: string;
  svg: string | null;
  play: PlayData | null;
  entry: DrillDiagramEntry;
}

function asEntry(g: DrillData['diagram']): DrillDiagramEntry | null {
  if (!g) return null;
  if (typeof g === 'string') {
    const t = g.trimStart();
    return t.toLowerCase().startsWith('<svg') || t.startsWith('<?xml') ? { svg: g } : null;
  }
  return g.svg || g.play ? g : null;
}

function sameDiagram(a: DrillDiagramEntry, b: DrillDiagramEntry): boolean {
  if (a.svg || b.svg) return a.svg === b.svg;
  return JSON.stringify(a.play ?? null) === JSON.stringify(b.play ?? null);
}

function view(e: DrillDiagramEntry): DiagramView {
  const p = e.play;
  const play = p && Array.isArray(p.players) && Array.isArray(p.lines) ? p : null;
  return { label: (e.label ?? '').trim(), svg: e.svg ?? (play ? buildDrillSVG(play) : null), play, entry: e };
}

/**
 * All of a drill's diagrams, first to last. A drill from before multiple
 * diagrams existed reads as a list of one (or none) — nothing is migrated.
 */
export function drillDiagrams(d: DrillData | null | undefined): DiagramView[] {
  if (!d) return [];
  const one = asEntry(d.diagram);
  if (!Array.isArray(d.diagrams) || !d.diagrams.length) return one ? [view(one)] : [];
  const list = d.diagrams.map((g) => asEntry(g)).filter((e): e is DrillDiagramEntry => !!e);
  // A build that only knows `diagram` (the native app before this change) can
  // edit or remove the first picture without touching `diagrams`. `diagram` is
  // then the newer word on the first entry: removed → drop it; redrawn or
  // replaced → use it, keeping its label.
  if (!one) return list.slice(1).map(view);
  const head = list[0];
  if (head && !sameDiagram(head, one)) list[0] = { ...one, ...(head.label && !one.label ? { label: head.label } : {}) };
  return list.map(view);
}

/**
 * Write a list of diagrams back onto a drill. `diagram` always becomes the
 * first entry (or null). `diagrams` is only kept when it says something
 * `diagram` alone can't — two or more, or a label — so a plain one-diagram
 * drill saves byte-for-byte as it always has.
 */
export function withDiagrams(d: DrillData, list: DrillDiagramEntry[]): DrillData {
  const clean = list
    .map((e): DrillDiagramEntry => {
      const label = (e.label ?? '').trim();
      const { label: _drop, ...rest } = e;
      return label ? { ...rest, label } : rest;
    })
    .filter((e) => e.svg || e.play);
  const { diagrams: _old, ...base } = d;
  const first = clean[0] ?? null;
  const out: DrillData = { ...base, diagram: first };
  if (clean.length > 1 || clean.some((e) => e.label)) out.diagrams = clean;
  return out;
}

/** "Inside Zone" → "Inside Zone (copy)", "Inside Zone (copy)" → "Inside Zone (copy 2)", … */
export function copyName(name: string, taken: Iterable<string> = []): string {
  const used = new Set([...taken].map((n) => n.trim().toLowerCase()));
  const base = (name.trim() || 'Untitled').replace(/\s*\(copy(?: \d+)?\)$/i, '');
  for (let n = 1; ; n++) {
    const cand = `${base} (${n === 1 ? 'copy' : 'copy ' + n})`;
    if (!used.has(cand.toLowerCase())) return cand;
  }
}

/**
 * A fresh, independent copy of a drill or play script for "Duplicate". Deep
 * cloned so editing the copy can never touch the original, with the new id and
 * a "(copy)" name. The shared-library link is dropped: the copy is the team's
 * own drill, so the library still counts the original as the one added.
 */
export function duplicateDrill(d: DrillData, newId: string, taken: Iterable<string> = []): DrillData {
  const copy = JSON.parse(JSON.stringify(d)) as DrillData;
  delete copy.catalogId;
  return { ...copy, id: newId, name: copyName(d.name, taken) };
}
